import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  NEIGHBORHOOD_MIN_SEARCH_CHARS,
  type NeighborhoodOption,
} from '../../../../../services/mapsNeighborhoods';

export type NeighborhoodPick = {
  placeId: string;
  displayLabel: string;
};

export type NeighborhoodSearchInputProps = {
  id?: string;
  cityName: string | null | undefined;
  isError: boolean;
  onSelectionChange: (value: NeighborhoodPick | null) => void;
  loadSuggestions: (searchTerm: string) => Promise<NeighborhoodOption[]>;
};

const DEBOUNCE_MS = 1000;

export function NeighborhoodSearchInput({
  id = 'neighborhood',
  cityName,
  isError,
  onSelectionChange,
  loadSuggestions,
}: NeighborhoodSearchInputProps) {
  const listboxId = useId();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<NeighborhoodOption[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ignoreNextChangeToCommitRef = useRef(false);
  const lastCommitDisplayRef = useRef('');

  const canSearch =
    Boolean(cityName && String(cityName).trim()) &&
    text.trim().length >= NEIGHBORHOOD_MIN_SEARCH_CHARS;

  const runSearch = useCallback(
    async (search: string) => {
      if (!cityName || search.trim().length < NEIGHBORHOOD_MIN_SEARCH_CHARS) {
        setResults([]);
        return;
      }

      setLoading(true);
      try {
        setResults(await loadSuggestions(search));
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [cityName, loadSuggestions]
  );

  useEffect(() => {
    setText('');
    setResults([]);
    setOpen(false);
  }, [cityName]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!canSearch) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      void runSearch(text);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [text, canSearch, runSearch]);

  const selectPlace = (place: NeighborhoodOption) => {
    if (!place.id?.trim() || !place.name?.trim()) return;
    lastCommitDisplayRef.current = place.name;
    ignoreNextChangeToCommitRef.current = true;
    setText(place.name);
    setOpen(false);
    onSelectionChange({
      placeId: place.id,
      displayLabel: place.name,
    });
  };

  const handleInputChange = (v: string) => {
    if (ignoreNextChangeToCommitRef.current) {
      ignoreNextChangeToCommitRef.current = false;
      if (v === lastCommitDisplayRef.current) {
        return;
      }
    }
    onSelectionChange(null);
    setText(v);
    if (v.trim().length >= NEIGHBORHOOD_MIN_SEARCH_CHARS) setOpen(true);
  };

  const onBlur = () => {
    blurCloseRef.current = setTimeout(() => setOpen(false), 150);
  };

  const onFocus = () => {
    if (blurCloseRef.current) clearTimeout(blurCloseRef.current);
    if (text.trim().length >= NEIGHBORHOOD_MIN_SEARCH_CHARS && results.length) setOpen(true);
  };

  const tooltipId = `${id}-search-help`;
  const neighborhoodHelpText = cityName
    ? `Digite pelo menos ${NEIGHBORHOOD_MIN_SEARCH_CHARS} caracteres para ver sugestões. Escolha um bairro na lista para habilitar o botão Buscar agendas.`
    : 'Selecione uma cidade para habilitar a busca por bairro.';

  const showList = open && (loading || results.length > 0);
  const inputBorder = isError
    ? 'border-[color:var(--feedback-negative,#c62828)]'
    : 'border-[color:var(--gray-400,#ccc)]';

  return (
    <div className="form-group form-group-neighborhood relative box-border min-w-0 w-full max-w-full">
      <div className="label-inline-row mb-2 flex items-center gap-1.5">
        <label htmlFor={id} className="m-0 block cursor-default leading-none text-inherit">
          Bairro
        </label>
        <span className="group/tooltip relative inline-flex shrink-0 items-center">
          <button
            type="button"
            className="inline-flex h-[14px] w-[14px] shrink-0 cursor-help items-center justify-center rounded-full border border-[color:var(--gray-300)] bg-transparent p-0 text-[9px] font-semibold italic leading-none text-[color:var(--gray-500)] hover:border-[color:var(--gray-400)] hover:text-[color:var(--gray-700)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ins-blue)]"
            aria-label="Ajuda sobre busca por bairro"
            aria-describedby={tooltipId}
          >
            i
          </button>
          <span
            id={tooltipId}
            role="tooltip"
            className="pointer-events-none invisible absolute bottom-[calc(100%+8px)] left-1/2 z-30 w-[min(280px,calc(100vw-48px))] -translate-x-1/2 rounded-md bg-[color:var(--gray-900)] px-3 py-2.5 text-left text-xs font-normal leading-snug text-white shadow-md opacity-0 transition-[opacity,visibility] duration-150 group-hover/tooltip:visible group-hover/tooltip:opacity-100 group-hover/tooltip:pointer-events-auto group-focus-within/tooltip:visible group-focus-within/tooltip:opacity-100 group-focus-within/tooltip:pointer-events-auto"
          >
            {neighborhoodHelpText}
            <span
              className="absolute left-1/2 top-full -ml-1.5 border-[6px] border-transparent border-t-[color:var(--gray-900)]"
              aria-hidden
            />
          </span>
        </span>
      </div>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listboxId}
        aria-autocomplete="list"
        autoComplete="off"
        placeholder="Digite o bairro"
        value={text}
        disabled={!cityName}
        className={`box-border h-[42px] w-full min-w-0 max-w-[225px] rounded border px-2.5 text-inherit placeholder:text-[color:var(--gray-500)] disabled:cursor-not-allowed disabled:bg-[color:var(--gray-100)] disabled:text-[color:var(--gray-500)] disabled:placeholder:text-[color:var(--gray-500)] md:max-w-none ${inputBorder}`}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault();
        }}
      />
      <ul
        id={listboxId}
        role="listbox"
        hidden={!showList}
        className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 m-0 max-h-60 list-none overflow-y-auto rounded border border-[color:var(--gray-400)] bg-white p-1 shadow-md"
      >
        {loading ? (
          <li className="cursor-default px-2.5 py-2 text-sm">Buscando…</li>
        ) : (
          results.map((p) => (
            <li
              key={p.id}
              role="option"
              aria-selected="false"
              className="cursor-pointer px-2.5 py-2 text-sm leading-snug hover:bg-[color:var(--gray-100)]"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectPlace(p)}
            >
              {p.name}
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
